import {
  BlockerFunction,
  useBlocker,
  useNavigate,
  Location,
} from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigationBlockers } from './NavigationBlocker';
import { NavigateAwayModal } from './NavigateAwayModal';

export const NavigateAwayPrompt = () => {
  const { has: hasBlockers, getHasBlockers } = useNavigationBlockers();
  const [isPromptVisible, setIsPromptVisible] = useState(false);
  const isPromptVisibleRef = useRef(false);
  const navigate = useNavigate();

  const clearLocationState = useCallback(
    (location: Location<any>) => {
      const { state, pathname, search, hash } = location;
      const newState = { ...state };
      delete newState.noBlocker;

      navigate(pathname + search + hash, {
        replace: true,
        state: newState,
      });
    },
    [navigate],
  );

  const handleNavigateAway: BlockerFunction = useCallback(
    ({ currentLocation, nextLocation }) => {
      // Navigation with query params is allowed
      if (currentLocation.pathname === nextLocation.pathname) return false;

      // If query params contains noBlocker, we can navigate away and clear the state
      if (nextLocation.state?.noBlocker) {
        // Schedule the state clearing in a microtask

        if (currentLocation.pathname !== nextLocation.pathname)
          queueMicrotask(() => clearLocationState(nextLocation));
        return false;
      }

      // If there are no blockers, we can navigate away.
      // We use the store to fetch up to date hasBlockers state to prevent stale references
      if (!getHasBlockers()) return false;

      // If the prompt is already visible, don't show it again.
      // Use a ref so this check doesn't cause the blocker function to be
      // re-registered with React Router when the prompt becomes visible,
      // which would otherwise allow a concurrent navigation to slip through.
      if (isPromptVisibleRef.current) return false;

      isPromptVisibleRef.current = true;
      setIsPromptVisible(true);
      return true;
    },
    [clearLocationState, getHasBlockers],
  );
  const blocker = useBlocker(handleNavigateAway);

  const handleOk = useCallback(() => {
    isPromptVisibleRef.current = false;
    setIsPromptVisible(false);
    blocker.proceed?.();
  }, [blocker]);

  const handleCancel = useCallback(() => {
    isPromptVisibleRef.current = false;
    setIsPromptVisible(false);
    blocker.reset?.();
  }, [blocker]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasBlockers) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasBlockers]);

  if (!isPromptVisible) return null;

  return <NavigateAwayModal onOk={handleOk} onCancel={handleCancel} />;
};
